import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "@/redux/axios";

interface LoginSignupState {
  user: {
    user_id?: string;
    email?: string;
    full_name?: string | null;
  } | null;
  token: string | null;
  authenticated: boolean;
  email: string;
  otpScreen: boolean;
  isFetching: boolean;
  isInitiateSuccess: boolean;
  isVerifySuccess: boolean;
  isError: boolean;
  errorMessage: string;
  canResend: boolean;
  resendTimer: number;
  isUserValid: boolean | null; // Store is_valid from initiate response
}

const initialState: LoginSignupState = {
  user: null,
  token: null,
  authenticated: false,
  email: "",
  otpScreen: false,
  isFetching: false,
  isInitiateSuccess: false,
  isVerifySuccess: false,
  isError: false,
  errorMessage: "",
  canResend: false,
  resendTimer: 0,
  isUserValid: null,
};

export const initiateOTP = createAsyncThunk(
  "loginSignup/initiate",
  async (email: string, thunkAPI) => {
    try {
      const response = await axios.post("/auth/initiate", {
        email,
      });

      if (response.status === 200 || response.status === 201) {
        return { data: response.data, email };
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Network error" }
      );
    }
  }
);

export const verifyLoginOTP = createAsyncThunk(
  "loginSignup/verifyLoginOTP",
  async ({ email, otp }: { email: string; otp: string }, thunkAPI) => {
    try {
      const response = await axios.post("/auth/login/verify-otp", {
        email,
        otp,
      });

      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Network error" }
      );
    }
  }
);

export const verifySignupOTP = createAsyncThunk(
  "loginSignup/verifySignupOTP",
  async (
    {
      email,
      otp,
      referral_code,
      source,
    }: {
      email: string;
      otp: string;
      referral_code?: string | null;
      source?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const response = await axios.post("/auth/signup/verify-otp", {
        email,
        otp,
        referral_code: referral_code || null,
        source: source || null,
      });

      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Network error" }
      );
    }
  }
);

export const resendOTP = createAsyncThunk(
  "loginSignup/resendOTP",
  async (email: string, thunkAPI) => {
    try {
      const response = await axios.post("/auth/initiate", {
        email,
      });

      if (response.status === 200 || response.status === 201) {
        return { data: response.data, email };
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Network error" }
      );
    }
  }
);

export const loginSignupSlice = createSlice({
  name: "loginSignup",
  initialState,
  reducers: {
    setError: (state, action: PayloadAction<string>) => {
      state.isError = true;
      state.errorMessage = action.payload;
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = "";
    },
    clearState: (state) => {
      state.isFetching = false;
      state.otpScreen = false;
      state.isError = false;
      state.errorMessage = "";
      state.isInitiateSuccess = false;
      state.isVerifySuccess = false;
      state.isUserValid = null;
    },
    setResendTimer: (state, action: PayloadAction<number>) => {
      state.resendTimer = action.payload;
      state.canResend = action.payload === 0;
    },
    resetResendTimer: (state) => {
      state.resendTimer = 10;
      state.canResend = false;
    },
    clearEmail: (state) => {
      state.email = "";
      state.otpScreen = false;
      state.isUserValid = null;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.authenticated = false;
      state.email = "";
      state.otpScreen = false;
      state.isFetching = false;
      state.isInitiateSuccess = false;
      state.isVerifySuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.canResend = false;
      state.resendTimer = 0;
      state.isUserValid = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initiate OTP
      .addCase(initiateOTP.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
        state.isInitiateSuccess = false;
      })
      .addCase(initiateOTP.fulfilled, (state, action) => {
        state.isFetching = false;
        state.isInitiateSuccess = true;
        state.otpScreen = true;
        state.email = action.payload.email;
        state.resendTimer = 10;
        state.canResend = false;
        // Store is_valid from response
        state.isUserValid = action.payload.data?.is_valid ?? null;
      })
      .addCase(initiateOTP.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload?.message || "Failed to send OTP";
      })
      // Verify Login OTP
      .addCase(verifyLoginOTP.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
        state.isVerifySuccess = false;
      })
      .addCase(verifyLoginOTP.fulfilled, (state, action) => {
        state.isFetching = false;
        state.isVerifySuccess = true;
        state.user = action.payload?.user || null;
        state.token = action.payload?.token || null;
        state.authenticated = action.payload?.authenticated || false;
        state.otpScreen = false;
      })
      .addCase(verifyLoginOTP.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        const payload = action.payload;
        if (payload?.error?.otp) {
          state.errorMessage = payload.error.otp;
        } else if (typeof payload?.error === 'string') {
          state.errorMessage = payload.error;
        } else {
          state.errorMessage = payload?.message || "Invalid OTP";
        }
      })
      // Verify Signup OTP
      .addCase(verifySignupOTP.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
        state.isVerifySuccess = false;
      })
      .addCase(verifySignupOTP.fulfilled, (state, action) => {
        state.isFetching = false;
        state.isVerifySuccess = true;
        state.user = action.payload?.user || null;
        state.token = action.payload?.token || null;
        state.authenticated = action.payload?.authenticated || false;
        state.otpScreen = false;
      })
      .addCase(verifySignupOTP.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        const payload = action.payload;
        if (payload?.error?.otp) {
          state.errorMessage = payload.error.otp;
        } else if (typeof payload?.error === 'string') {
          state.errorMessage = payload.error;
        } else {
          state.errorMessage = payload?.message || "Invalid OTP";
        }
      })
      // Resend OTP
      .addCase(resendOTP.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(resendOTP.fulfilled, (state, action) => {
        state.isFetching = false;
        state.resendTimer = 10;
        state.canResend = false;
        // Update is_valid from resend response
        state.isUserValid = action.payload.data?.is_valid ?? state.isUserValid;
      })
      .addCase(resendOTP.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload?.message || "Failed to resend OTP";
      });
  },
});

export const {
  clearState,
  clearError,
  setError,
  setResendTimer,
  resetResendTimer,
  clearEmail,
  logout,
} = loginSignupSlice.actions;

export const loginSignupSelector = (state: any) => state.loginSignup;

export default loginSignupSlice.reducer;
